import {type CheckoutOpenOptions, type CloseFlags, expressCheckoutInstance} from './sdk-checkout';

interface SDKOptions {
	/**
	 * The URL to the Express Checkout iframe
	 */
	checkoutURL: string;

	/**
	 * The container fragment for the Express Checkout iframe
	 */
	checkoutContainerFragment: string;

	/**
	 * @deprecated Express Checkout iframe loading is click-only. This option is retained for integration compatibility.
	 */
	checkoutLoadMode?: 'eager' | 'lazy';

	/**
	 * Whether the checkout iframe's inline checkout_data includes a full cart calculation response.
	 */
	checkoutEmbedsCartCalculationResponse?: boolean;

	/**
	 * Error reporter function. This function is called when an error occurs in the SDK.
	 */
	errorReporter: (error: unknown) => void;
}

/**
 * SDK to control adding, manipulating, and removing the PeachPay Express Checkout buttons
 */
function peachpaySDK(config: SDKOptions) {
	if (!config.checkoutURL) {
		throw new Error('Integration error. A Express Checkout URL must be supplied.');
	}

	const sdkContext = {
		checkoutURL: config.checkoutURL,
		checkoutContainerFragment: config.checkoutContainerFragment,
		checkoutLoadMode: config.checkoutLoadMode ?? 'lazy',
		checkoutEmbedsCartCalculationResponse: config.checkoutEmbedsCartCalculationResponse === true,
		errorReporter: config.errorReporter,
	} satisfies SDKOptions;

	const checkoutAPI = expressCheckoutInstance(sdkContext);

	return {
		/**
		 * Activates all PeachPay buttons on the page that do not have the `data-activation-trigger` attribute defined. If the attribute is defined then the button is activated by alternative means.
		 */
		activateButtons() {
			document.addEventListener('click', async event => {
				const $target = event.target as HTMLElement;
				if (!$target) {
					return;
				}

				const $button = $target?.closest<HTMLButtonElement>('[data-peachpay-button]');
				if (!$button || $button?.dataset['activationTrigger']) {
					return;
				}

				event.preventDefault();

				if ($button.classList.contains('loading')) {
					return;
				}

				$button.classList.add('loading');
				await checkoutAPI.open();
				$button.classList.remove('loading');
			});
		},
		async showCheckout(options?: CheckoutOpenOptions): Promise<void> {
			await checkoutAPI.open(options);
		},
		preloadCheckout(): void {
			checkoutAPI.preload();
		},
		showCheckoutShell(): number {
			const openAttemptId = checkoutAPI.show();
			const mountPromise: Promise<void> = checkoutAPI.maybeMount({revealOnLoad: false}).catch(config.errorReporter);
			void mountPromise;

			return openAttemptId;
		},
		isOpenAttemptActive(openAttemptId: number): boolean {
			return checkoutAPI.isOpenAttemptActive(openAttemptId);
		},
		hideCheckout(): void {
			checkoutAPI.hide();
		},
		onClose(callback: (event: CloseFlags) => void): void {
			checkoutAPI.onClose(callback);
		},
	};
}

type PeachPaySDK = ReturnType<typeof peachpaySDK>;

export {
	type SDKOptions,
	type PeachPaySDK,

	peachpaySDK,
};
